import React, { useState, useEffect } from 'react'
import Layout from '../../../../components/vision/Layout'
import ProtectedRoute from '../../../../components/vision/ProtectedRoute'
import IndicadorModal from './components/IndicadorModal'

export default function IndicadoresPage() {
  const [indicadores, setIndicadores] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<any>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    tipo: 'quantidade',
    categoria: 'funil',
    tipo_indicador: 'lancamento',
    formula_calculo: null,
    indicadores_dependentes: null,
    ativo: true
  })

  useEffect(() => {
    fetchIndicadores()
  }, [])

  const fetchIndicadores = () => {
    fetch('/api/gestao/kpis/indicador-tipos')
      .then(res => res.json())
      .then(data => setIndicadores(data))
      .finally(() => setLoading(false))
  }

  const handleCreate = () => {
    setEditing(null)
    setFormData({ 
      nome: '', 
      descricao: '', 
      tipo: 'quantidade', 
      categoria: 'funil',
      tipo_indicador: 'lancamento',
      formula_calculo: null,
      indicadores_dependentes: null,
      ativo: true 
    })
    setShowModal(true)
  }

  const handleEdit = (indicador: any) => {
    setEditing(indicador)
    setFormData(indicador)
    setShowModal(true)
  }

  const handleSubmit = async () => {
    const method = editing ? 'PUT' : 'POST'
    
    // IMPORTANTE: Definir calculo_automatico baseado no tipo_indicador
    const dataToSend = {
      ...formData,
      calculo_automatico: formData.tipo_indicador === 'calculado'
    }
    
    const body = editing ? { id: editing.id, ...dataToSend } : dataToSend
    
    const res = await fetch('/api/gestao/kpis/indicador-tipos', {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })

    if (res.ok) {
      setShowModal(false)
      fetchIndicadores()
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Deseja realmente excluir este indicador?')) return
    
    const res = await fetch(`/api/gestao/kpis/indicador-tipos?id=${id}`, {
      method: 'DELETE'
    })

    if (res.ok) fetchIndicadores()
  }

  const filteredIndicadores = indicadores.filter((ind: any) => {
    const searchLower = searchTerm.toLowerCase()
    return (
      ind.nome?.toLowerCase().includes(searchLower) ||
      ind.descricao?.toLowerCase().includes(searchLower) ||
      ind.categoria?.toLowerCase().includes(searchLower)
    )
  })

  const getTipoIcon = (tipo: string) => {
    switch(tipo) {
      case 'numero': return 'M7 20l4-16m2 16l4-16M6 9h14M4 15h14'
      case 'percentual': return 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10'
      case 'moeda': return 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
      default: return 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
    }
  }

  return (
    <ProtectedRoute>
      <Layout>
        <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-4xl font-bold text-gray-800 mb-2">Indicadores de Performance</h1>
                <p className="text-gray-600">Gerencie os tipos de indicadores disponíveis no sistema</p>
              </div>
              <button
                onClick={handleCreate}
                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-xl hover:from-blue-600 hover:to-blue-700 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Novo Indicador
              </button>
            </div>
          </div>

          {/* Search and Stats */}
          <div className="mb-6 grid grid-cols-12 gap-6">
            <div className="col-span-8">
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Buscar indicador por nome, descrição ou categoria..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-12 py-4 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-md transition-all"
                  />
                  <svg className="absolute left-4 top-4.5 w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-4 top-4.5 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                {/* View Mode Toggle */}
                <div className="flex bg-white rounded-xl shadow-md border border-gray-200 p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`px-4 py-3 rounded-lg transition-all ${
                      viewMode === 'grid' 
                        ? 'bg-blue-500 text-white shadow-md' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    title="Visualização em grade"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-4 py-3 rounded-lg transition-all ${
                      viewMode === 'list' 
                        ? 'bg-blue-500 text-white shadow-md' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    title="Visualização em lista"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div className="col-span-4 grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg">
                <div className="text-3xl font-bold">{indicadores.length}</div>
                <div className="text-blue-100 text-sm mt-1">Total</div>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white shadow-lg">
                <div className="text-3xl font-bold">{indicadores.filter((i: any) => i.ativo).length}</div>
                <div className="text-green-100 text-sm mt-1">Ativos</div>
              </div>
            </div>
          </div>

          {/* Indicadores Grid/List */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Carregando indicadores...</p>
              </div>
            </div>
          ) : filteredIndicadores.length > 0 ? (
            <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-4"}>
              {filteredIndicadores.map((ind: any) => (
                <div
                  key={ind.id}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 overflow-hidden border border-gray-100 group"
                >
                  <div className={`h-2 ${ind.ativo ? 'bg-gradient-to-r from-green-400 to-green-500' : 'bg-gray-300'}`}></div>
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          ind.ativo ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                          <svg className={`w-6 h-6 ${ind.ativo ? 'text-blue-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={getTipoIcon(ind.tipo)} />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-gray-800 group-hover:text-blue-600 transition-colors">
                            {ind.nome}
                          </h3>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                            ind.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {ind.ativo ? (
                              <>
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Ativo
                              </>
                            ) : 'Inativo'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">
                      {ind.descricao}
                    </p>

                    <div className="flex items-center gap-2 mb-4">
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium">
                        {ind.tipo}
                      </span>
                      {ind.categoria && (
                        <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium">
                          {ind.categoria}
                        </span>
                      )}
                    </div>

                    <div className="flex gap-2 pt-4 border-t border-gray-100">
                      <button
                        onClick={() => handleEdit(ind)}
                        className="flex-1 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors font-medium text-sm flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(ind.id)}
                        className="flex-1 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-medium text-sm flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Excluir
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg p-16 text-center">
              <svg className="w-24 h-24 mx-auto mb-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={searchTerm ? "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" : "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"} />
              </svg>
              <h3 className="text-2xl font-bold text-gray-700 mb-2">
                {searchTerm ? 'Nenhum indicador encontrado' : 'Nenhum indicador cadastrado'}
              </h3>
              <p className="text-gray-500 mb-6">
                {searchTerm ? 'Tente buscar por outro termo' : 'Clique no botão "Novo Indicador" para começar'}
              </p>
              {!searchTerm && (
                <button
                  onClick={handleCreate}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-xl hover:from-blue-600 hover:to-blue-700 shadow-lg transition-all inline-flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Criar Primeiro Indicador
                </button>
              )}
            </div>
          )}

          <IndicadorModal
            show={showModal}
            editing={editing}
            formData={formData}
            indicadores={indicadores}
            onClose={() => setShowModal(false)}
            onSubmit={handleSubmit}
            onChange={setFormData}
          />
        </div>
      </Layout>
    </ProtectedRoute>
  )
}
